import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale/pt-BR';
import Select from "react-select";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import {
  LancamentoFinanceiro,
  CreateLancamentoFinanceiroData,
  UpdateLancamentoFinanceiroData,
  LancamentoFinanceiroFilters,
  EstornarLancamentoData,
  TipoLancamento,
  FormaPagamento,
  ContaBancaria,
  CategoriaFinanceira
} from '../../../types/financeiro';
import ScrollableTable from '../contracts/components/ScrollableTable';
import GerarRelatorioModal from './components/GerarRelatorioModal';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MONTHS_SHORT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};

registerLocale('pt-BR', customPtBR as any);

// Opções para os selects
const tipoLancamentoOptions = [
  { value: 'RECEITA', label: 'Receita' },
  { value: 'DESPESA', label: 'Despesa' },
  { value: 'TRANSFERENCIA', label: 'Transferência' }
];

// Temporário: Opções fixas para formas de pagamento (depois será dinâmico via API)
const formaPagamentoOptions = [
  { value: 1, label: 'PIX' },
  { value: 2, label: 'Cartão de Crédito' },
  { value: 3, label: 'Cartão de Débito' },
  { value: 4, label: 'Boleto' },
  { value: 5, label: 'Transferência Bancária' },
  { value: 6, label: 'Dinheiro' }
];

const LancamentosFinanceiros = () => {
  // Estados principais
  const [lancamentos, setLancamentos] = useState<LancamentoFinanceiro[]>([]);
  const [filteredLancamentos, setFilteredLancamentos] = useState<LancamentoFinanceiro[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Estados para formulários
  const [lancamentoSelecionado, setLancamentoSelecionado] = useState<LancamentoFinanceiro | null>(null);
  const [formData, setFormData] = useState<CreateLancamentoFinanceiroData>({
    data_lancamento: '',
    descricao: '',
    valor: 0,
    tipo: 'RECEITA'
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateLancamentoFinanceiroData>({});
  const [estornoData, setEstornoData] = useState<EstornarLancamentoData>({
    motivo_estorno: ''
  });

  // Estados para datas
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedDateEdit, setSelectedDateEdit] = useState<Date | null>(null);
  const [dataInicio, setDataInicio] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dataFim, setDataFim] = useState<Date | null>(new Date());

  // Estados para opções dos selects
  const [categorias, setCategorias] = useState<{ value: number, label: string }[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<{ value: number, label: string }[]>([]);

  // Estados para controle
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [estornoId, setEstornoId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isEstornando, setIsEstornando] = useState<boolean>(false);

  // Estados para controle de erros nos modais
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [estornoError, setEstornoError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Estados para resumo financeiro
  const [resumoFinanceiro, setResumoFinanceiro] = useState({
    entradas: 0,
    saidas: 0,
    saldo_atual: 0
  });

  // Estado para modal de exportação
  const [showExportModal, setShowExportModal] = useState(false);

  // Função para fechar modal programaticamente
  const forceCloseModal = (modalId: string): void => {
    document.body.classList.remove('modal-open');
    document.body.removeAttribute('data-bs-overflow');
    document.body.removeAttribute('data-bs-padding-right');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');

    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }

    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');
      modalElement.classList.remove('show');
    }
  };

  // Função para formatar moeda
  const formatCurrency = (value: number): string => {
    const numericValue = parseFloat(value?.toString() || '0') || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numericValue);
  };

  // Função para obter cor do tipo de lançamento
  const getTipoColor = (tipo: TipoLancamento): string => {
    switch (tipo) {
      case 'RECEITA': return 'text-success';
      case 'DESPESA': return 'text-danger';
      case 'TRANSFERENCIA': return 'text-info';
      default: return 'text-secondary';
    }
  };

  // Função para obter ícone do tipo de lançamento
  const getTipoIcon = (tipo: TipoLancamento): string => {
    switch (tipo) {
      case 'RECEITA': return 'ti ti-trending-up';
      case 'DESPESA': return 'ti ti-trending-down';
      case 'TRANSFERENCIA': return 'ti ti-arrows-exchange';
      default: return 'ti ti-minus';
    }
  };

  const buscarSaldoAtualizado = async (): Promise<number> => {
    try {
      const contasResponse = await apiService.getContasBancarias();

      if (contasResponse.success && contasResponse.data) {
        let contasArray: any[] = [];

        if (Array.isArray(contasResponse.data)) {
          contasArray = contasResponse.data;
        } else {
          const responseData = contasResponse.data as any;
          contasArray = responseData.data || [];
        }

        const contaPrincipal = contasArray.find(conta => conta.principal == 1);

        if (contaPrincipal) {
          return parseFloat(contaPrincipal.saldo_atual) || 0;
        }
      }

      return 0;
    } catch (error) {
      console.error('Erro ao buscar saldo:', error);
      return 0;
    }
  };

  // Função para calcular resumo financeiro
  const calcularResumoFinanceiro = (lancamentosData: LancamentoFinanceiro[], saldoConta: number) => {
    let entradas = 0;
    let saidas = 0;

    lancamentosData
      .filter(lancamento => lancamento.situacao === 'CONFIRMADO')
      .forEach(lancamento => {
        // Garantir que o valor seja um número válido
        const valor = parseFloat(lancamento.valor?.toString() || '0') || 0;
        
        if (lancamento.tipo === 'RECEITA') {
          entradas += valor;
        } else if (lancamento.tipo === 'DESPESA') {
          saidas += valor;
        }
        // TRANSFERENCIA não entra no cálculo de entradas/saídas
      });

    setResumoFinanceiro({
      entradas: isNaN(entradas) ? 0 : entradas,
      saidas: isNaN(saidas) ? 0 : saidas,
      saldo_atual: isNaN(saldoConta) ? 0 : saldoConta
    });
  };

  // Buscar dados iniciais
  const fetchDadosIniciais = async () => {
    try {
      setIsLoading(true);

      // Buscar categorias financeiras
      try {
        const categoriasResponse = await apiService.getCategoriaFinanceira();

        if (categoriasResponse.success && categoriasResponse.data) {
          let categoriasArray: CategoriaFinanceira[] = [];

          if (Array.isArray(categoriasResponse.data)) {
            categoriasArray = categoriasResponse.data as CategoriaFinanceira[];
          } else {
            const responseData = categoriasResponse.data as any;
            categoriasArray = responseData.data || [];
          }

          const categoriasOptions = categoriasArray.map((categoria: CategoriaFinanceira) => ({
            value: categoria.id,
            label: categoria.nome
          }));
          setCategorias(categoriasOptions);
        }
      } catch (err) {
        console.error('Erro ao buscar categorias:', err);
      }

      // Buscar formas de pagamento
      try {
        const formasResponse = await apiService.getFormasPagamento();
        if (formasResponse.success && formasResponse.data) {
          let formasArray: any[] = [];

          if (Array.isArray(formasResponse.data)) {
            formasArray = formasResponse.data;
          } else {
            const responseData = formasResponse.data as any;
            formasArray = responseData.data || [];
          }

          const formasOptions = formasArray
            .filter(forma => forma.ativo)
            .map(forma => ({
              value: forma.id,
              label: forma.nome
            }));
          setFormasPagamento(formasOptions);
        }
      } catch (err) {
        console.error('Erro ao buscar formas de pagamento:', err);
        // Fallback para as opções fixas caso a API não esteja funcionando
        setFormasPagamento(formaPagamentoOptions);
      }

      // Buscar lançamentos
      await fetchLancamentos();

    } catch (err) {
      console.error('Erro ao buscar dados iniciais:', err);
      setError('Erro ao carregar dados iniciais');
    } finally {
      setIsLoading(false);
    }
  };

  // Buscar lançamentos
  const fetchLancamentos = async () => {
    try {
      const filtros: LancamentoFinanceiroFilters = {
        data_inicio: dataInicio ? dataInicio.toISOString().split('T')[0] : undefined,
        data_fim: dataFim ? dataFim.toISOString().split('T')[0] : undefined,
        resumo: true,
        ordenar_por: 'data_lancamento',
        ordem: 'DESC'
      };

      // Buscar lançamentos
      const response = await apiService.getLancamentosFinanceiros(filtros);

      // Buscar saldo atual da conta bancária
      let saldoContaAtual = 0;
      try {
        const contasResponse = await apiService.getContasBancarias();

        if (contasResponse.success && contasResponse.data) {
          let contasArray: any[] = [];

          if (Array.isArray(contasResponse.data)) {
            contasArray = contasResponse.data;
          } else {
            const responseData = contasResponse.data as any;
            contasArray = responseData.data || [];
          }

          const contaPrincipal = contasArray.find(conta => conta.principal == 1);

          if (contaPrincipal) {
            saldoContaAtual = parseFloat(contaPrincipal.saldo_atual) || 0;
          }
        }
      } catch (errConta) {
        console.error('Erro ao buscar saldo da conta:', errConta);
      }

      if (response.success && response.data) {
        let lancamentosData: LancamentoFinanceiro[] = [];

        if (Array.isArray(response.data)) {
          lancamentosData = response.data as LancamentoFinanceiro[];
        } else {
          const responseData = response.data as any;
          if (responseData.data && Array.isArray(responseData.data)) {
            lancamentosData = responseData.data as LancamentoFinanceiro[];
          } else {
            console.error('Estrutura de dados inesperada:', response.data);
            setError('Estrutura de dados inesperada da API');
            return;
          }
        }

        const lancamentosComKey = lancamentosData.map((lancamento: any) => ({
          ...lancamento,
          key: lancamento.id.toString(),
          valor: parseFloat(lancamento.valor?.toString() || '0') || 0,
          saldo_conta_apos: saldoContaAtual
        }));

        setLancamentos(lancamentosComKey);
        setFilteredLancamentos(lancamentosComKey);

        // Calcular resumo financeiro
        calcularResumoFinanceiro(lancamentosData, saldoContaAtual);
      } else {
        console.error('Resposta sem sucesso ou sem dados:', response);
        setError('Não foi possível obter os dados dos lançamentos');
      }
    } catch (err) {
      console.error('Erro ao buscar lançamentos:', err);
      setError('Erro ao carregar dados dos lançamentos');
    }
  };

  // Buscar lançamentos quando as datas mudarem
  useEffect(() => {
    if (dataInicio && dataFim) {
      fetchLancamentos();
    }
  }, [dataInicio, dataFim]);

  useEffect(() => {
    fetchDadosIniciais();
  }, []);

  // Filtro de pesquisa com debounce
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredLancamentos(lancamentos);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = lancamentos.filter(lancamento => {
        const searchableFields = [
          lancamento.descricao,
          lancamento.tipo,
          lancamento.situacao,
          lancamento.pessoa_nome,
          lancamento.categoria_nome,
          lancamento.conta_bancaria_nome,
          formatCurrency(lancamento.valor)
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredLancamentos(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, lancamentos]);

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para editar lançamento
  const handleEditLancamento = async (id: number) => {
    try {
      setEditError(null); // ADICIONAR ESTA LINHA
      const lancamento = lancamentos.find(l => l.id === id);
      if (lancamento) {
        console.log('Lançamento selecionado para edição:', lancamento);
        setLancamentoSelecionado(lancamento);

        // Converter id_categoria para number se vier como string
        let categoriaId = lancamento.id_categoria;
        if (typeof categoriaId === 'string') {
          categoriaId = parseInt(categoriaId);
        }

        console.log('ID da categoria:', categoriaId);
        console.log('Categorias disponíveis:', categorias);

        setUpdateFormData({
          descricao: lancamento.descricao,
          valor: lancamento.valor,
          tipo: lancamento.tipo,
          id_forma_pagamento: lancamento.id_forma_pagamento,
          id_categoria: categoriaId,
          observacoes: lancamento.observacoes
        });

        // Data corrigida
        const dataCorrigida = new Date(lancamento.data_lancamento + 'T00:00:00');
        setSelectedDateEdit(dataCorrigida);
      }
    } catch (err) {
      console.error('Erro ao preparar edição:', err);
      setEditError('Erro ao carregar dados do lançamento'); // USAR setEditError
    }
  };

  // Função para submeter edição
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lancamentoSelecionado) return;

    try {
      setIsLoading(true);
      setEditError(null); // LIMPAR ERRO ANTERIOR

      // Garantir que a data seja formatada corretamente na edição
      let dataFormatada = undefined;
      if (selectedDateEdit) {
        const year = selectedDateEdit.getFullYear();
        const month = String(selectedDateEdit.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateEdit.getDate()).padStart(2, '0');
        dataFormatada = `${year}-${month}-${day}`;
      }

      const dados = {
        ...updateFormData,
        data_lancamento: dataFormatada,
        id_conta_bancaria: 1 // FIXO: sempre usar conta principal
      };

      const response = await apiService.updateLancamentoFinanceiro(lancamentoSelecionado.id, dados);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_edit .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        await fetchLancamentos();
        toast.success('Lançamento atualizado com sucesso!');
      } else {
        setEditError('Erro ao atualizar lançamento: ' + response.message); // USAR setEditError
      }
    } catch (err: any) {
      console.error('Erro ao atualizar lançamento:', err);
      setEditError('Erro ao atualizar lançamento: ' + (err.message || 'Erro desconhecido')); // USAR setEditError
    } finally {
      setIsLoading(false);
    }
  };

  // Função para criar lançamento
  const handleCreateLancamento = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      // Formatação da data corrigida
      let dataFormatada = '';
      if (selectedDate) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        dataFormatada = `${year}-${month}-${day}`;
      }

      const dados: CreateLancamentoFinanceiroData = {
        ...formData,
        data_lancamento: dataFormatada,
        id_conta_bancaria: 1 // FIXO: sempre usar conta principal
      };

      const response = await apiService.createLancamentoFinanceiro(dados);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_add .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        // Resetar formulário
        setFormData({
          data_lancamento: '',
          descricao: '',
          valor: 0,
          tipo: 'RECEITA'
        });
        setSelectedDate(new Date());

        // Recarregar lançamentos E saldo atualizado
        await fetchLancamentos();
        toast.success('Lançamento criado com sucesso!');
      } else {
        setError('Erro ao criar lançamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar lançamento:', err);
      setError('Erro ao criar lançamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar lançamento
  const handleDeleteLancamento = (id: number) => {
    setDeleteId(id);
    setDeleteError(null); // Limpar erro anterior
  };

  // Confirmar exclusão
  const confirmDeleteLancamento = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      setDeleteError(null); // Limpar erro anterior

      const response = await apiService.deleteLancamentoFinanceiro(deleteId);

      if (response.success) {
        forceCloseModal('delete_lancamento');
        await fetchLancamentos();
        toast.success("Lançamento excluído com sucesso!");
        setDeleteId(null);
      } else {
        setDeleteError('Erro ao excluir lançamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao excluir lançamento:', err);
      setDeleteError('Erro ao excluir lançamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para estornar lançamento
  const handleEstornarLancamento = (id: number) => {
    setEstornoId(id);
    setEstornoError(null); // Limpar erro anterior
    setEstornoData({ motivo_estorno: '' }); // Limpar formulário
  };

  // Confirmar estorno
  const confirmEstornarLancamento = async () => {
    // Validação mais robusta do ID e motivo
    if (!estornoId || estornoId <= 0) {
      console.error('ID do estorno inválido:', estornoId);
      setEstornoError('ID do lançamento é inválido');
      return;
    }

    if (!estornoData.motivo_estorno || !estornoData.motivo_estorno.trim()) {
      console.error('Motivo do estorno não informado');
      setEstornoError('Motivo do estorno é obrigatório');
      return;
    }

    try {
      setIsEstornando(true);
      setEstornoError(null); // Limpar erro anterior

      const response = await apiService.estornarLancamento(estornoId, estornoData);

      if (response.success) {
        forceCloseModal('estorno_lancamento');

        // Limpar dados
        setEstornoData({ motivo_estorno: '' });
        setEstornoId(null);

        // Recarregar lançamentos
        await fetchLancamentos();
        toast.success("Lançamento estornado com sucesso!");
      } else {
        setEstornoError('Erro ao estornar lançamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao estornar lançamento:', err);
      setEstornoError('Erro ao estornar lançamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsEstornando(false);
    }
  };

  // Função para manipular mudanças no formulário
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'valor' ? parseFloat(value) || 0 : value
    });
  };

  // Função para manipular mudanças no formulário de edição
  const handleUpdateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUpdateFormData({
      ...updateFormData,
      [name]: name === 'valor' ? parseFloat(value) || 0 : value
    });
  };

  // Definir as colunas da tabela
  const columns = [
    {
      title: "Data",
      dataIndex: "data_lancamento",
      key: "data_lancamento",
      sorter: true,
      render: (text: string) => {
        // Adicionar 'T00:00:00' para evitar problemas de timezone
        const date = new Date(text + 'T00:00:00');
        return (
          <div className="d-flex align-items-center">
            <span>{date.toLocaleDateString('pt-BR')}</span>
          </div>
        );
      }
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      key: "descricao",
      render: (text: string, record: LancamentoFinanceiro) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">{text}</span>
          {record.categoria_nome && (
            <small className="text-muted">{record.categoria_nome}</small>
          )}
          {record.pessoa_nome && (
            <small className="text-muted">{record.pessoa_nome}</small>
          )}
        </div>
      ),
    },
    {
      title: "Situação",
      dataIndex: "situacao",
      key: "situacao",
      render: (text: string, record: LancamentoFinanceiro) => (
        <div className="d-flex align-items-center gap-2">
          <i className={`${getTipoIcon(record.tipo)} ${getTipoColor(record.tipo)}`}></i>
          <div className="d-flex flex-column">
            <span className={`badge badge-pill ${text === 'CONFIRMADO' ? 'bg-success' : 'bg-danger'}`}>
              {text === 'CONFIRMADO' ? 'Confirmado' : 'Estornado'}
            </span>
            <small className={`${getTipoColor(record.tipo)} fw-semibold`}>
              {record.tipo === 'RECEITA' ? 'Receita' :
                record.tipo === 'DESPESA' ? 'Despesa' : 'Transferência'}
            </small>
          </div>
        </div>
      ),
      sorter: true,
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      sorter: true,
      render: (value: number, record: LancamentoFinanceiro) => (
        <div className="text-end">
          <div className={`fw-bold ${getTipoColor(record.tipo)}`}>
            {record.tipo === 'RECEITA' ? '+' : record.tipo === 'DESPESA' ? '-' : ''}
            {formatCurrency(value)}
          </div>
          {record.forma_pagamento_nome && (
            <small className="text-muted">
              {record.forma_pagamento_nome}
            </small>
          )}
        </div>
      ),
    },
    {
      title: "Saldo",
      dataIndex: "saldo_apos_transacao",
      key: "saldo_apos_transacao",
      render: (value: number, record: LancamentoFinanceiro) => {
        // Se o valor vier nulo (transações antigas não processadas), mostra um traço ou 0
        const saldoParaExibir = value !== undefined && value !== null ? value : 0;

        return (
          <div className="text-end">
            <div className={`fw-semibold ${saldoParaExibir < 0 ? 'text-danger' : 'text-primary'}`}>
              {formatCurrency(saldoParaExibir)}
            </div>
            {/* <small className="text-muted">Conta Principal</small> */}
          </div>
        );
      },
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      key: "acoes",
      render: (text: string, record: LancamentoFinanceiro) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvas_edit"
            onClick={() => handleEditLancamento(record.id)}
          >
            <i className="ti ti-edit"></i>
          </button>
          {record.situacao === 'CONFIRMADO' && (
            <button
              className="btn btn-sm btn-outline-warning"
              data-bs-toggle="modal"
              data-bs-target="#estorno_lancamento"
              onClick={() => handleEstornarLancamento(record.id)}
            >
              <i className="ti ti-refresh"></i>
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-danger"
            data-bs-toggle="modal"
            data-bs-target="#delete_lancamento"
            onClick={() => handleDeleteLancamento(record.id)}
          >
            <i className="ti ti-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Lançamentos Financeiros
                      <span className="count-title">{filteredLancamentos.length} lançamentos</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards de Resumo Financeiro */}
              <div className="row mb-4">
                <div className="col-xl-4 col-sm-6">
                  <div className="card bg-success-light border-success">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-success">
                          <i className="ti ti-trending-up"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Entradas</div>
                          <div className="dash-counts">
                            <p>{formatCurrency(resumoFinanceiro.entradas)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-success fw-bold">
                          Receitas Confirmadas
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-4 col-sm-6">
                  <div className="card bg-danger-light border-danger">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-danger">
                          <i className="ti ti-trending-down"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Saídas</div>
                          <div className="dash-counts">
                            <p>{formatCurrency(resumoFinanceiro.saidas)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-danger fw-bold">
                          Despesas Confirmadas
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-4 col-sm-12">
                  <div className="card bg-primary-light border-primary">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-primary">
                          <i className="ti ti-wallet"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Saldo Atual</div>
                          <div className="dash-counts">
                            <p>{formatCurrency(resumoFinanceiro.saldo_atual)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-primary fw-bold">
                          Conta Bancária Principal
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Lançamentos"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <button
                          className="btn btn-outline-secondary me-2"
                          onClick={() => setShowExportModal(true)}
                        >
                          <i className="ti ti-file-export me-2"></i>
                          Exportar
                        </button>
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-plus me-2"></i>
                          Novo Lançamento
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtro de Período */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <h5 className="card-title mb-0">Filtro de Período</h5>
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label">Data Início</label>
                              <div className="icon-form">
                                <span className="form-icon">
                                  <i className="ti ti-calendar"></i>
                                </span>
                                <DatePicker
                                  className="form-control"
                                  selected={dataInicio}
                                  onChange={(date) => setDataInicio(date)}
                                  dateFormat="dd/MM/yyyy"
                                  locale="pt-BR"
                                  placeholderText="dd/mm/aaaa"
                                  popperClassName="date-picker-higher-z-index"
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Data Fim</label>
                              <div className="icon-form">
                                <span className="form-icon">
                                  <i className="ti ti-calendar"></i>
                                </span>
                                <DatePicker
                                  className="form-control"
                                  selected={dataFim}
                                  onChange={(date) => setDataFim(date)}
                                  dateFormat="dd/MM/yyyy"
                                  locale="pt-BR"
                                  placeholderText="dd/mm/aaaa"
                                  popperClassName="date-picker-higher-z-index"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de Lançamentos */}
                  {isLoading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2">Carregando lançamentos...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : !isSearching && searchText && filteredLancamentos.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-search-off fs-3 text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhum lançamento encontrado</h5>
                      <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                      <button
                        className="btn btn-outline-primary mt-2"
                        onClick={handleClearSearch}
                      >
                        <i className="ti ti-x me-1"></i>
                        Limpar pesquisa
                      </button>
                    </div>
                  ) : (
                    <ScrollableTable dataSource={filteredLancamentos} columns={columns} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS para melhorar o z-index do DatePicker */}
      <style>
        {`
          .date-picker-higher-z-index {
            z-index: 9999 !important;
          }
          .react-datepicker-popper {
            z-index: 9999 !important;
          }
          .react-datepicker {
            z-index: 9999 !important;
          }
        `}
      </style>

      {/* Modal Adicionar Lançamento */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Lançamento</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateLancamento}>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Data do Lançamento <span className="text-danger">*</span>
                  </label>
                  <div className="icon-form">
                    <span className="form-icon">
                      <i className="ti ti-calendar-check" />
                    </span>
                    <DatePicker
                      className="form-control datetimepicker"
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      placeholderText="dd/mm/aaaa"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Tipo <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={tipoLancamentoOptions}
                    className="select"
                    placeholder="Escolher tipo"
                    classNamePrefix="react-select"
                    value={tipoLancamentoOptions.find(opt => opt.value === formData.tipo)}
                    onChange={(option) => setFormData({ ...formData, tipo: option?.value as TipoLancamento })}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Valor <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="valor"
                    value={formData.valor}
                    onChange={handleFormChange}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleFormChange}
                    required
                    placeholder="Descrição do lançamento"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Forma de Pagamento
                  </label>
                  <Select
                    options={formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions}
                    className="select"
                    placeholder="Escolher forma"
                    classNamePrefix="react-select"
                    value={(formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions).find(opt => opt.value === formData.id_forma_pagamento)}
                    onChange={(option) => setFormData({ ...formData, id_forma_pagamento: option?.value as number })}
                    isClearable
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Categoria
                  </label>
                  <Select
                    options={categorias}
                    className="select"
                    placeholder="Escolher categoria"
                    classNamePrefix="react-select"
                    value={categorias.find(opt => opt.value === formData.id_categoria)}
                    onChange={(option) => setFormData({ ...formData, id_categoria: option?.value })}
                    isClearable
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Observações
                  </label>
                  <textarea
                    className="form-control"
                    name="observacoes"
                    value={formData.observacoes || ''}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Observações adicionais..."
                  />
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Criando...
                  </>
                ) : (
                  'Criar Lançamento'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Editar Lançamento */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_edit"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Editar Lançamento</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitEdit}>
              <div className="row">
                {editError && (
                  <div className="alert alert-danger mb-3" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {editError}
                  </div>
                )}
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Data do Lançamento <span className="text-danger">*</span>
                    </label>
                    <div className="icon-form">
                      <span className="form-icon">
                        <i className="ti ti-calendar-check" />
                      </span>
                      <DatePicker
                        className="form-control datetimepicker"
                        selected={selectedDateEdit}
                        onChange={(date) => setSelectedDateEdit(date)}
                        dateFormat="dd/MM/yyyy"
                        locale="pt-BR"
                        placeholderText="dd/mm/aaaa"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Tipo <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={tipoLancamentoOptions}
                      className="select"
                      placeholder="Escolher tipo"
                      classNamePrefix="react-select"
                      value={tipoLancamentoOptions.find(opt => opt.value === updateFormData.tipo)}
                      onChange={(option) => setUpdateFormData({ ...updateFormData, tipo: option?.value as TipoLancamento })}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Valor <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="valor"
                      value={updateFormData.valor || 0}
                      onChange={handleUpdateFormChange}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Descrição <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="descricao"
                      value={updateFormData.descricao || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Descrição do lançamento"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Forma de Pagamento
                    </label>
                    <Select
                      options={formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions}
                      className="select"
                      placeholder="Escolher forma"
                      classNamePrefix="react-select"
                      value={(formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions).find(opt => opt.value === updateFormData.id_forma_pagamento)}
                      onChange={(option) => setUpdateFormData({ ...updateFormData, id_forma_pagamento: option?.value as number })}
                      isClearable
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Categoria
                    </label>
                    <Select
                      options={categorias}
                      className="select"
                      placeholder="Escolher categoria"
                      classNamePrefix="react-select"
                      value={categorias.find(opt => opt.value === updateFormData.id_categoria)}
                      onChange={(option) => {
                        console.log('Categoria selecionada:', option);
                        setUpdateFormData({ ...updateFormData, id_categoria: option?.value });
                      }}
                      isClearable
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Observações
                    </label>
                    <textarea
                      className="form-control"
                      name="observacoes"
                      value={updateFormData.observacoes || ''}
                      onChange={handleUpdateFormChange}
                      rows={3}
                      placeholder="Observações adicionais..."
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal Confirmar Exclusão */}
      <div className="modal fade" id="delete_lancamento" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Excluir Lançamento?</h4>
                <p className="mb-0">
                  Tem certeza de que deseja excluir <br /> este lançamento?
                </p>

                {/* Exibir erro no modal se houver */}
                {deleteError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {deleteError}
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={() => setDeleteError(null)}
                  >
                    Cancelar
                  </Link>
                  <button
                    onClick={confirmDeleteLancamento}
                    className="btn btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Excluir'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Estornar Lançamento */}
      <div className="modal fade" id="estorno_lancamento" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Estornar Lançamento</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setEstornoError(null)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="text-center mb-4">
                <div className="avatar avatar-xl bg-warning-light rounded-circle mb-3">
                  <i className="ti ti-refresh fs-36 text-warning" />
                </div>
                <h5 className="mb-2">Estornar este lançamento?</h5>
                <p className="text-muted mb-0">
                  O lançamento será marcado como estornado e não afetará mais o saldo.
                </p>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Motivo do Estorno <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  value={estornoData.motivo_estorno}
                  onChange={(e) => setEstornoData({ ...estornoData, motivo_estorno: e.target.value })}
                  rows={3}
                  placeholder="Descreva o motivo do estorno..."
                  required
                />
              </div>

              {/* Exibir erro no modal se houver */}
              {estornoError && (
                <div className="alert alert-danger" role="alert">
                  <i className="ti ti-alert-circle me-2"></i>
                  {estornoError}
                </div>
              )}

              <div className="d-flex align-items-center justify-content-center mt-4">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => setEstornoError(null)}
                >
                  Cancelar
                </Link>
                <button
                  onClick={confirmEstornarLancamento}
                  className="btn btn-warning"
                  disabled={isEstornando || !estornoData.motivo_estorno.trim()}
                >
                  {isEstornando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Estornando...
                    </>
                  ) : (
                    'Confirmar Estorno'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GerarRelatorioModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        defaultDataInicio={dataInicio}
        defaultDataFim={dataFim}
      />
    </div>
  );
};

export default LancamentosFinanceiros;